<template>
    <page-loading v-if="loading"></page-loading>
    <!-- Outside corporate the OU sits in a team settings tab, which already provides the page layout. -->
    <component :is="onCorporate() ? VxPageLayout : 'div'" v-else :layout="onCorporate() ? 'sidebar' : undefined">
        <page-header v-if="!onCorporate()">
            <span data-test="org-unit-header">Details</span>
        </page-header>

        <template v-if="onCorporate()" v-slot:title>
            <span data-test="org-unit-header">{{ orgUnit?.name }}</span>
        </template>

        <template v-if="onCorporate()" v-slot:subtitle>
            <a :href="`/settings/teams/${teamId}#/org-units`" class="no-underline! text-sky-600! text-base font-normal">
                <font-awesome-icon widthAuto class="mr-1" :icon="faArrowLeft"></font-awesome-icon>
                All Organizational Units
            </a>
        </template>

        <template v-if="onCorporate()" v-slot:sidebar>
            <page-sidebar appearance="nested" :links="sidebarLinks" data-test="org-unit-sidebar"></page-sidebar>
        </template>

        <div v-if="!onCorporate()" class="max-w-(--breakpoint-sm)">
            <slot></slot>
        </div>
        <slot v-else></slot>
    </component>
</template>
<script setup>
import { VxPageLayout } from '@voxie/frontend-components';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import PageSidebar from '~/components/general/PageSidebar.vue';
import { watch, computed, onMounted, provide } from 'vue';
import { PAGE_HEADING_LEVEL } from '~/provides/pageHeadingLevel';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { useOrgUnitDetails } from './useOrgUnitDetails';
import { useCurrentGroup } from '~/composables/useCurrentGroup';

const props = defineProps({
    orgUnitId: {
        type: Number,
        required: true,
    },
});

const teamId = Spark.state.currentTeam.id;
const { onCorporate } = useCurrentGroup();

// Outside corporate the settings tab owns the layout, so the page-header inside is still the page's h1.
provide(PAGE_HEADING_LEVEL, onCorporate() ? 'h2' : 'h1');

const { mount, orgUnit, loading } = useOrgUnitDetails();

const sidebarLinks = computed(() => {
    const baseLinks = [
        { href: `/settings/teams/${teamId}/org-units/${props.orgUnitId}`, label: 'Details' },
        { href: `/settings/teams/${teamId}/org-units/${props.orgUnitId}/groups`, label: 'Groups' },
        {
            href: `/settings/teams/${teamId}/org-units/${props.orgUnitId}/group-collections`,
            label: 'Collections',
        },
        { href: `/settings/teams/${teamId}/org-units/${props.orgUnitId}/users`, label: 'Users' },
    ];

    // Guard against loading states
    if (loading.value || !orgUnit.value) {
        return baseLinks;
    }

    // Guard against non-billable org units
    if (!orgUnit.value.is_billable) {
        return baseLinks;
    }

    return [
        ...baseLinks,
        {
            href: `/settings/teams/${teamId}/org-units/${props.orgUnitId}/billing-details`,
            label: 'Billing Details',
        },
        {
            href: `/settings/teams/${teamId}/org-units/${props.orgUnitId}/proforma-invoices`,
            label: 'Upcoming Invoices',
        },
        {
            href: `/settings/teams/${teamId}/org-units/${props.orgUnitId}/historical-invoices`,
            label: 'Historical Invoices',
        },
    ];
});

onMounted(() => {
    mount(props.orgUnitId);
});

watch(
    () => props.orgUnitId,
    (newOrgUnitId, oldOrgUnitId) => {
        if (newOrgUnitId && newOrgUnitId !== oldOrgUnitId) {
            mount(newOrgUnitId);
        }
    }
);
</script>
